import { useEffect, useState } from "react";
import { Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import * as services from "../../services/saleng";
import { formatNumber } from "../../utils/formatNumber";

export default function SellProductDialog({ row, onClose, onSaved }) {
  const [options, setOptions] = useState([]);
  const [target, setTarget] = useState(null);
  const [mode, setMode] = useState("replace");
  const [weight, setWeight] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const remaining = Number(row?.remainingWeight ?? row?.weight ?? 0);
  const partiallyUsed = Math.abs(Number(row?.weight ?? 0) - remaining) > 0.000001;

  useEffect(() => {
    if (!row) return;
    let active = true;
    setTarget(null);
    setMode(Math.abs(Number(row.weight ?? 0) - Number(row.remainingWeight ?? row.weight ?? 0)) > 0.000001 ? "split" : "replace");
    setWeight("");
    setError("");
    setLoading(true);
    setOptions([]);
    services.getLockSellProducts().then((response) => {
      if (active) setOptions((response?.data?.data ?? []).filter(
        (product) => Number(product.id) !== Number(row.sellProductId ?? row.productId),
      ));
    }).catch((err) => {
      if (active) setError(err.response?.data?.error || "โหลดรายการสินค้าไม่สำเร็จ");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [row]);

  const save = async () => {
    if (!target || saving) return;
    const splitWeight = Number(weight);
    if (mode === "split" && (!Number.isFinite(splitWeight) || splitWeight <= 0 || splitWeight > remaining || splitWeight === Number(row.weight))) {
      setError("กรุณาระบุน้ำหนักมากกว่า 0 ไม่เกินน้ำหนักคงเหลือ หากเปลี่ยนทั้งหมดให้เลือกเปลี่ยนทั้งรายการ");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await services.convertLockSellProduct(row.id, {
        sellProductId: target.id, mode,
        ...(mode === "split" ? { weight: splitWeight } : {}),
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || "บันทึกการเปลี่ยนสินค้าไม่สำเร็จ");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={Boolean(row)} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>เปลี่ยนสินค้าที่ขาย (Manual)</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
          <Typography>ซื้อเป็นสินค้า: {row?.productName}</Typography>
          <Typography>น้ำหนักคงเหลือ {formatNumber(remaining)} กก.</Typography>
          <RadioGroup row value={mode} onChange={(event) => setMode(event.target.value)}>
            <FormControlLabel value="replace" control={<Radio />} label="เปลี่ยนทั้งรายการ" disabled={saving || partiallyUsed} />
            <FormControlLabel value="split" control={<Radio />} label="แบ่งเป็นรายการใหม่" disabled={saving} />
          </RadioGroup>
          {partiallyUsed && <Alert severity="info">รายการนี้ใช้ประกันไปแล้วบางส่วน แบ่งได้เฉพาะน้ำหนักคงเหลือ</Alert>}
          <Autocomplete
            options={options} value={target} loading={loading} disabled={saving || loading}
            onChange={(_, value) => setTarget(value)}
            getOptionLabel={(option) => `${option.code ? `${option.code} - ` : ""}${option.title || ""}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="ไม่พบสินค้าที่มีราคาสำนักงานใหญ่"
            renderInput={(params) => <TextField {...params} label="ขายเป็นสินค้า" required />}
          />
          {mode === "split" && <TextField
            label="น้ำหนักที่แบ่งไปสินค้าใหม่ (กก.)" type="number" value={weight}
            onChange={(event) => setWeight(event.target.value)} disabled={saving}
            inputProps={{ min: 0, max: remaining, step: "any" }}
            helperText="หักน้ำหนักจากรายการเดิมเท่ากับน้ำหนักสินค้าใหม่ (1:1)"
          />}
          <Typography>ราคาหน้าร้าน See: {formatNumber(row?.branchPrice ?? 0)} บาท/กก. (สินค้าต้นทาง)</Typography>
          {target && <Typography>ราคาหน้าร้าน Sor: {formatNumber(target.headOfficePrice)} บาท/กก. (สินค้าใหม่)</Typography>}
          <Typography variant="body2" color="text.secondary">ราคาขายจะกลับไปใช้ราคาอัตโนมัติของสินค้าใหม่ และคำนวณราคาพิเศษ ++ ใหม่</Typography>
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>ยกเลิก</Button>
        <Button variant="contained" onClick={save} disabled={!target || loading || saving}>
          {saving ? "กำลังบันทึก..." : "บันทึก"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
